import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../services/api'

export interface DashboardMetrics {
  kpis: {
    total_orders_today: number
    delivered_orders_today: number
    cancelled_orders_today: number
    cancellation_rate: number
    gross_revenue_today: number
    net_revenue_today: number
    avg_order_value: number
    completion_rate: number
    active_restaurants_today: number
    active_users_today: number
    delta_orders: number
    delta_revenue: number
    delivery_fee_revenue_today: number
  }
  operational_health: {
    avg_time_placed_to_delivered: number
    p90_placed_to_delivered: number
    late_orders_count: number
    failed_payments_count: number
  }
  top_restaurant: {
    id: string
    name: string
    orders_today: number
    revenue_today: number
    avg_order_value: number
    avg_fulfillment_time: number
    cancellation_rate: number
    is_active: boolean
  } | null
  top_restaurants: Array<{
    rank: number
    id: string
    name: string
    orders: number
    revenue: number
    avg_fulfillment_time: number
    cancel_rate: number
  }>
  top_customers: Array<{
    rank: number
    id: string
    name: string
    phone: string
    orders: number
    revenue: number
    cancel_rate: number
  }>
  live_feed: Array<{
    id: string
    order_number: string
    event_type: 'placed' | 'accepted' | 'rejected' | 'cancelled' | 'completed' | 'payment_failed'
    restaurant_name: string
    customer_area: string
    status: string
    total: number
    timestamp: string
    reason?: string
  }>
  alerts: Array<{
    type: 'high_cancel_rate' | 'slow_acceptance' | 'payment_failures' | 'no_driver' | 'restaurant_offline'
    severity: 'warning' | 'critical'
    message: string
    restaurant_id?: string
    restaurant_name?: string
    value?: number
    threshold?: number
  }>
}

interface UseDashboardReturn {
  metrics: DashboardMetrics | null
  loading: boolean
  error: string | null
  fetchMetrics: () => Promise<void>
  startAutoRefresh: (intervalMs?: number) => void
  stopAutoRefresh: () => void
  isAutoRefreshing: boolean
}

export const useDashboard = (): UseDashboardReturn => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false)
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null)

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await apiClient.dashboard.getMetrics()

      if (response.success && response.data) {
        setMetrics(response.data)
      } else {
        throw new Error(response.error || 'Failed to fetch dashboard metrics')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      console.error('Dashboard metrics fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const startAutoRefresh = useCallback((intervalMs: number = 30000) => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current)
    }

    setIsAutoRefreshing(true)
    fetchMetrics() // Initial fetch

    autoRefreshInterval.current = setInterval(() => {
      fetchMetrics()
    }, intervalMs)
  }, [fetchMetrics])

  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current)
      autoRefreshInterval.current = null
    }
    setIsAutoRefreshing(false)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current)
      }
    }
  }, [])

  return {
    metrics,
    loading,
    error,
    fetchMetrics,
    startAutoRefresh,
    stopAutoRefresh,
    isAutoRefreshing
  }
}
